var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { deriveRowId, evaluatePredicate, evaluateRowOpRestriction, } from "@hyper-hyper-space/hhs3_rdb";
const AUTH_FAILURE_PATTERNS = [
    'does not satisfy ALLOW',
    'canObserve predicate rejected',
    'canDeploy predicate rejected',
    'must be authored',
    'rowAuthor = $author',
];
const BIND_AUTHOR_REQUIRED_MESSAGES = new Set([
    'ALTER SCHEMA requires an author identity',
    'ADD SCHEMA requires BY when the database declares creators',
    'ADD TABLEGROUP requires BY when the database declares creators',
    'ADD SCHEMA requires an author when the database declares creators',
    'ADD TABLEGROUP requires an author when the database declares creators',
]);
export function isBindAuthorRequiredFailure(diagnostics) {
    return diagnostics.some((d) => d.code === 'BIND_UNKNOWN_NAME' && BIND_AUTHOR_REQUIRED_MESSAGES.has(d.message));
}
export function hasExplicitByAst(stmt) {
    return stmt.author !== undefined;
}
export function isBindAuthorRetryStatement(stmt) {
    return stmt.kind === 'alter-schema' || stmt.kind === 'add-member';
}
export function labelForKeyId(session, keyId) {
    var _a;
    const record = (_a = session.keyVault) === null || _a === void 0 ? void 0 : _a.list().find((key) => key.keyId === keyId);
    if (record !== undefined)
        return `$${record.label}`;
    return `#${keyId.slice(0, 8)}`;
}
export function formatAuthorHint(candidates) {
    if (candidates.length === 0)
        return undefined;
    const labels = candidates.map((c) => `$${c.label}`);
    if (labels.length === 1)
        return `hint: BY ${labels[0]}`;
    return `hint: BY ${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}
function toCandidate(session, record) {
    return {
        keyId: record.keyId,
        label: record.label,
        unlocked: session.isUnlocked(record.keyId),
    };
}
function candidateFromIdentity(session, identity) {
    var _a, _b;
    const record = (_a = session.keyVault) === null || _a === void 0 ? void 0 : _a.list().find((key) => key.keyId === identity.keyId);
    return {
        keyId: identity.keyId,
        label: (_b = record === null || record === void 0 ? void 0 : record.label) !== null && _b !== void 0 ? _b : identity.keyId,
        unlocked: session.isUnlocked(identity.keyId),
    };
}
export function scanKeystore(session, test) {
    return __awaiter(this, void 0, void 0, function* () {
        if (session.keyVault === undefined)
            return [];
        const out = [];
        for (const record of session.keyVault.list()) {
            if (yield test(record.keyId))
                out.push(toCandidate(session, record));
        }
        return out;
    });
}
export function resolveAuthorForGate(session, test, preferred, options) {
    return __awaiter(this, void 0, void 0, function* () {
        const doScanKeystore = (options === null || options === void 0 ? void 0 : options.scanKeystore) !== false;
        const rejected = [];
        const seen = new Set();
        for (const identity of preferred) {
            if (identity === undefined || seen.has(identity.keyId))
                continue;
            seen.add(identity.keyId);
            const candidate = candidateFromIdentity(session, identity);
            if (!(yield test(identity.keyId))) {
                rejected.push(candidate);
                continue;
            }
            if (session.isUnlocked(identity.keyId)) {
                return {
                    identity,
                    candidates: doScanKeystore ? yield scanKeystore(session, test) : [],
                    rejected: rejected.length > 0 ? rejected : undefined,
                };
            }
            return {
                locked: candidate,
                candidates: [],
                rejected: rejected.length > 0 ? rejected : undefined,
            };
        }
        if (!doScanKeystore) {
            return { candidates: [], rejected: rejected.length > 0 ? rejected : undefined };
        }
        const candidates = yield scanKeystore(session, test);
        const unlocked = candidates.find((c) => c.unlocked);
        if (unlocked !== undefined) {
            const identity = session.resolveIdentity(unlocked.label);
            if (identity !== undefined)
                return { identity, candidates };
        }
        const locked = candidates.find((c) => !c.unlocked);
        return { locked, candidates };
    });
}
export function evaluateObserveGateKey(observer, foreignGroupId, refAt, refFrom, keyId) {
    return __awaiter(this, void 0, void 0, function* () {
        return observer.evaluateObserveGate(foreignGroupId, keyId, refAt, refFrom);
    });
}
export function evaluateCanDeployKey(group, at, keyId) {
    return __awaiter(this, void 0, void 0, function* () {
        const canDeploy = group.getCanDeploy();
        if (canDeploy === undefined)
            return true;
        return evaluatePredicate(canDeploy, {
            getTableView: (table) => __awaiter(this, void 0, void 0, function* () { return group.makeTable(table).getView(at, at); }),
            getForeignTableView: (groupName, table) => group.resolveForeignTableView(groupName, table, at, at),
            author: keyId,
            context: 'object',
        });
    });
}
function rowOpFromBound(bound, keyId) {
    switch (bound.kind) {
        case 'insert':
            return {
                action: 'insert',
                rowId: deriveRowId(bound.uuid, keyId),
                uuid: bound.uuid,
                values: bound.values,
                author: keyId,
            };
        case 'update':
            return { action: 'update', rowId: bound.rowId, values: bound.values, author: keyId };
        case 'delete':
            return { action: 'delete', rowId: bound.rowId, author: keyId };
    }
}
export function evaluateRowRestrictionKey(bound, keyId) {
    return __awaiter(this, void 0, void 0, function* () {
        const group = bound.table.group;
        const at = bound.at;
        const groupView = yield group.getView(at, at);
        const schemaView = groupView.getSchemaView();
        const table = bound.table.tableName;
        const op = rowOpFromBound(bound, keyId);
        return evaluateRowOpRestriction(op, table, schemaView, (name) => groupView.getTableView(name), (groupName, name) => group.resolveForeignTableView(groupName, name, at, at));
    });
}
export function resolveObserveAuthor(session, observer, foreignGroupId, refAt, refFrom, preferred, options) {
    return __awaiter(this, void 0, void 0, function* () {
        return resolveAuthorForGate(session, (keyId) => evaluateObserveGateKey(observer, foreignGroupId, refAt, refFrom, keyId), preferred, options);
    });
}
function isAuthRelatedFailure(diagnostics) {
    return diagnostics.some((d) => d.code === 'VALIDATION_REJECTED'
        && AUTH_FAILURE_PATTERNS.some((pattern) => d.message.includes(pattern)));
}
export function isAuthRetryBound(bound) {
    switch (bound.kind) {
        case 'insert':
        case 'update':
        case 'delete':
        case 'update-ref':
        case 'update-schema':
            return true;
        default:
            return false;
    }
}
export function hasExplicitBy(bound) {
    return bound.ast.author !== undefined;
}
export function boundWithAuthor(bound, author) {
    return Object.assign(Object.assign({}, bound), { author });
}
function gateTestForBound(bound) {
    return __awaiter(this, void 0, void 0, function* () {
        switch (bound.kind) {
            case 'insert':
            case 'update':
            case 'delete':
                return (keyId) => evaluateRowRestrictionKey(bound, keyId);
            case 'update-ref': {
                const group = bound.group.group;
                if (group === undefined)
                    throw new Error('UPDATE REF target group is not loaded');
                const foreignId = resolveForeignGroupId(group, bound.ref);
                if (foreignId === undefined)
                    throw new Error(`Unknown bound group '${bound.ref}'`);
                const at = bound.at;
                return (keyId) => evaluateObserveGateKey(group, foreignId, at, at, keyId);
            }
            case 'update-schema': {
                const group = bound.group.group;
                if (group === undefined)
                    throw new Error('UPDATE SCHEMA target group is not loaded');
                return (keyId) => evaluateCanDeployKey(group, bound.at, keyId);
            }
        }
    });
}
export function resolveAuthorForBoundFailure(session, bound) {
    return __awaiter(this, void 0, void 0, function* () {
        const test = yield gateTestForBound(bound);
        return resolveAuthorForGate(session, test, [bound.author, yield session.currentAuthor()]);
    });
}
function resolveForeignGroupId(group, ref) {
    const bindings = group.getBindings();
    if (Object.prototype.hasOwnProperty.call(bindings, ref))
        return bindings[ref];
    if (Object.values(bindings).includes(ref))
        return ref;
    return undefined;
}
export function suggestAuthorsForFailure(session, bound, diagnostics) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!isAuthRelatedFailure(diagnostics))
            return undefined;
        let candidates = [];
        switch (bound.kind) {
            case 'insert':
            case 'update':
            case 'delete':
                candidates = yield scanKeystore(session, (keyId) => evaluateRowRestrictionKey(bound, keyId));
                break;
            case 'update-ref': {
                const group = bound.group.group;
                if (group === undefined)
                    break;
                const foreignId = resolveForeignGroupId(group, bound.ref);
                if (foreignId === undefined)
                    break;
                const at = bound.at;
                candidates = yield scanKeystore(session, (keyId) => evaluateObserveGateKey(group, foreignId, at, at, keyId));
                break;
            }
            case 'update-schema': {
                const group = bound.group.group;
                if (group === undefined)
                    break;
                candidates = yield scanKeystore(session, (keyId) => evaluateCanDeployKey(group, bound.at, keyId));
                break;
            }
            default:
                break;
        }
        return formatAuthorHint(candidates);
    });
}
export function resolveAuthorsForAlterSchema(session, stmt, context) {
    return __awaiter(this, void 0, void 0, function* () {
        const schema = yield context.resolveSchema(stmt.schema);
        if (schema.schema === undefined)
            return { candidates: [] };
        const at = yield context.resolveVersion(stmt.at, { kind: 'schema', id: schema.id, schema: schema.schema });
        const view = yield schema.schema.getView(at, at);
        return resolveAuthorForGate(session, (keyId) => Promise.resolve(view.isCreator(keyId)), [yield session.currentAuthor()]);
    });
}
export function resolveAuthorsForAddMember(session, stmt, context) {
    return __awaiter(this, void 0, void 0, function* () {
        const database = yield context.resolveDatabase(stmt.database);
        if (database.db === undefined || database.db.getCreators().length === 0)
            return { candidates: [] };
        return resolveAuthorForGate(session, (keyId) => Promise.resolve(database.db.isCreator(keyId)), [yield session.currentAuthor()]);
    });
}
function resolveAuthorsForBindStatement(session, stmt, context) {
    return __awaiter(this, void 0, void 0, function* () {
        switch (stmt.kind) {
            case 'alter-schema':
                return resolveAuthorsForAlterSchema(session, stmt, context);
            case 'add-member':
                return resolveAuthorsForAddMember(session, stmt, context);
            default:
                return undefined;
        }
    });
}
export function suggestAuthorsForBindFailure(session, stmt, diagnostics, context) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!isBindAuthorRequiredFailure(diagnostics))
            return undefined;
        try {
            const resolution = yield resolveAuthorsForBindStatement(session, stmt, context);
            if (resolution === undefined)
                return undefined;
            return formatAuthorHint(resolution.candidates);
        }
        catch (_a) {
            return undefined;
        }
    });
}
